import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@Utils/cn';
import OverviewPanel from '@Views/fish-details/panels/OverviewPanel';
import ActivityPanel from '@Views/fish-details/panels/ActivityPanel';
import MessagesPanel from '@Views/fish-details/panels/MessagesPanel';
import TagsPanel from '@Views/fish-details/panels/TagsPanel';
import ProfilePanel from '@Views/fish-details/panels/ProfilePanel';
import TeamProfilePanel from '@Views/fish-details/panels/TeamProfilePanel';
import MembershipPanel from '@Views/fish-details/panels/MembershipPanel';
import EmploymentPanel from '@Views/fish-details/panels/EmploymentPanel';
import { GuestDetailsHeader } from '../GuestDetailsHeader';
import type {
  GuestDetailsTab,
  GuestDetailsVariant,
  GuestSpendSummary,
  GuestTabOption,
} from '../types';

export interface GuestDetailsContainerProps {
  /** Tabs to render. They always appear in the fish details page's order. */
  tabs: GuestDetailsTab[];
  /** Falls back to the first rendered tab when not in `tabs`. */
  activeTab: GuestDetailsTab;
  onTabChange: (tab: GuestDetailsTab) => void;
  variant?: GuestDetailsVariant;
  /** Activity sub-tab; held internally when omitted. */
  activitySubTab?: string;
  onActivitySubTabChange?: (subTab: string) => void;
  spendSummary?: GuestSpendSummary;
  className?: string;
  headerClassName?: string;
  /** Panels render fragments, so spacing between their sections goes here. */
  panelClassName?: string;
}

const TAB_CONFIG: GuestTabOption<GuestDetailsTab>[] = [
  {
    value: 'overview',
    label: 'Overview',
    icon: <Sparkles className="size-4" />,
  },
  { value: 'activity', label: 'Activity' },
  { value: 'messages', label: 'Messages' },
  { value: 'tags', label: 'Tags & Segments' },
  { value: 'profile', label: 'Profile' },
  { value: 'membership', label: 'Membership' },
  { value: 'employment', label: 'Employment' },
];

/**
 * The fish details page's tab rail and panels. Must render inside a
 * FishDetailsProvider, which the panels read from.
 */
export function GuestDetailsContainer({
  tabs,
  activeTab,
  onTabChange,
  variant = 'guest',
  activitySubTab,
  onActivitySubTabChange,
  spendSummary,
  className,
  headerClassName,
  panelClassName,
}: GuestDetailsContainerProps) {
  const [localSubTab, setLocalSubTab] = useState('history');
  const subTab = activitySubTab ?? localSubTab;
  const setSubTab = onActivitySubTabChange ?? setLocalSubTab;

  const visibleTabs = TAB_CONFIG.filter((tab) => tabs.includes(tab.value));
  const currentTab = tabs.includes(activeTab)
    ? activeTab
    : visibleTabs[0]?.value;

  return (
    <div
      className={
        className ??
        'flex w-full flex-col gap-2.5 bg-aoh-surface px-8 pt-4 pb-6 shadow-2xl dark:bg-zinc-700'
      }
    >
      <GuestDetailsHeader
        tabs={visibleTabs}
        activeTab={currentTab}
        onTabChange={onTabChange}
        spendSummary={spendSummary}
        className={headerClassName}
      />
      <div className={cn('space-y-4', panelClassName)}>
        {currentTab === 'overview' && <OverviewPanel variant={variant} />}
        {currentTab === 'activity' && (
          <ActivityPanel subTab={subTab} onSubTabChange={setSubTab} />
        )}
        {currentTab === 'messages' && <MessagesPanel />}
        {currentTab === 'tags' && <TagsPanel />}
        {currentTab === 'profile' &&
          (variant === 'team-member' ? <TeamProfilePanel /> : <ProfilePanel />)}
        {currentTab === 'membership' && <MembershipPanel />}
        {currentTab === 'employment' && <EmploymentPanel />}
      </div>
    </div>
  );
}
